/**
 * Global axios response interceptors
 * ==================================
 * Extracted from App.js so the root component stays focused on rendering.
 *
 * What lives here:
 *   - Auto-retry on 502/503/520 (cold starts, server restarts)
 *   - 401 double-verification (prevent false-positive logout on transient DB blips)
 *   - Silent email reporting of 5xx / network errors to `/api/report-error`
 *   - Rate-limit toast (429) and timeout toast on mutations
 *   - A tiny "server error overlay" trigger (3+ server errors in 30s)
 *
 * Callbacks are installed by App.js via:
 *   - setSessionExpiredHandler(fn)  — called when the 401 re-check confirms expiry
 *   - setServerErrorHandler(fn)     — called when overlay should appear
 *
 * `serverErrorActive` is exported so background pollers can self-pause while
 * the overlay is up.
 */

import axios from 'axios';
import { toast } from '../utils/toast';
import { getCsrfToken } from './api';

// --------- handlers installed by the App component ---------
let _onSessionExpired = null;
let _onShowServerError = null;

export const setSessionExpiredHandler = (handler) => {
  _onSessionExpired = handler;
};

export const setServerErrorHandler = (handler) => {
  _onShowServerError = handler;
};

// Exported so pollers can check and skip while the overlay is visible
export let serverErrorActive = false;
export const clearServerErrorActive = () => {
  serverErrorActive = false;
};

// --------- internal state ---------
const _errorThrottle = { server: 0, rateLimit: 0, timeout: 0, auth: 0 };
let _serverErrorTimestamps = [];

// Non-critical polling/background endpoints — transient failures are expected, stay silent
export const _silentEndpoints = [
  '/api/report-error',
  '/api/notifications/unread-count',
  '/api/notifications',
  '/api/messages/unread-count',
  '/api/auth/me',
  '/api/heartbeat',
  '/api/user/profile-completion',
  '/api/hero/earnings',
  '/api/job-applications',
  '/api/jobs/selected',
  '/api/jobs/archived',
  '/api/announcements',
  '/api/task-hero/profile',
  '/api/users/should-show-onboarding',
  '/api/verifications/selfie/status',
  '/api/conversations',
];

// Monitored endpoints — silent toasts but STILL send error emails so we can verify fix
const _monitoredEndpoints = ['/api/heartbeat'];

// --------- helpers ---------
/**
 * Fire-and-forget error report. Uses navigator.sendBeacon when available so the
 * request:
 *   - Doesn't block the page or trigger an XHR error in DevTools console on 5xx
 *   - Survives page unload (queued by the browser)
 *   - Doesn't go through axios interceptors (no recursion risk)
 *
 * Falls back to `fetch` with `keepalive: true` when sendBeacon is missing.
 */
function _sendErrorReport(payload) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/report-error`;
    const json = JSON.stringify(payload);
    const blob = new Blob([json], { type: 'application/json' });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon returns false if the user agent refuses to queue (e.g., payload too big)
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    // Fallback — keepalive ensures the request survives a page navigation
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch (_) {
    // Reporting must never throw
  }
}

// --------- helpers ---------
function _trackServerError() {
  const now = Date.now();
  _serverErrorTimestamps = _serverErrorTimestamps.filter((t) => now - t < 30000);
  _serverErrorTimestamps.push(now);
  if (_serverErrorTimestamps.length >= 3 && _onShowServerError) {
    serverErrorActive = true;
    _onShowServerError();
    _serverErrorTimestamps = [];
  }
}

function _buildErrorPayload(statusCode, requestUrl, error) {
  const cfg = error?.config || {};
  const resp = error?.response || {};
  // Error reports intentionally run without user PII — the session cookie
  // is httpOnly and not readable from JS, and we avoid caching user data
  // in localStorage. Emails are populated server-side from the session.
  const user = null;

  // Sanitize request body — strip passwords and tokens
  let requestBody = null;
  if (cfg.data) {
    try {
      const parsed = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data;
      const sanitized = { ...parsed };
      ['password', 'new_password', 'old_password', 'token', 'session_token', 'card_number', 'cvv', 'cvc'].forEach((k) => {
        if (sanitized[k]) sanitized[k] = '***';
      });
      requestBody = sanitized;
    } catch (_) {
      requestBody = '[non-JSON body]';
    }
  }

  return {
    status_code: statusCode,
    url: requestUrl,
    message: resp?.data?.detail || error?.message || 'Unknown error',
    method: (cfg.method || 'unknown').toUpperCase(),
    page: window.location.pathname + window.location.search,
    user_email: user?.email || null,
    user_id: user?.id || null,
    user_role: user?.role || null,
    request_body: requestBody,
    response_body: typeof resp?.data === 'object'
      ? resp.data
      : (resp?.data ? String(resp.data).slice(0, 500) : null),
    response_headers: resp?.headers
      ? {
        'content-type': resp.headers['content-type'],
        'x-request-id': resp.headers['x-request-id'],
        'retry-after': resp.headers['retry-after'],
      }
      : null,
    client_time: new Date().toISOString(),
    online: navigator.onLine,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    error_code: error?.code || null,
  };
}

// --------- install interceptors (call once at app boot) ---------
let _installed = false;

export function installAxiosInterceptors() {
  if (_installed) return;
  _installed = true;

  axios.defaults.withCredentials = true;

  // CSRF: mirror the csrf_token cookie into a header on every unsafe request
  axios.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const token = getCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = token;
      }
    }
    return config;
  });

  // CSRF: retry once on 403 — the failed response sets/refreshes the cookie
  axios.interceptors.response.use(null, async (error) => {
    const config = error.config;
    const detail = error?.response?.data?.detail || '';
    if (error?.response?.status === 403 && typeof detail === 'string' && detail.startsWith('CSRF') && config && !config._csrfRetried) {
      config._csrfRetried = true;
      const token = getCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = token;
        return axios(config);
      }
    }
    return Promise.reject(error);
  });

  // Auto-retry on 502/503/520 (cold start / server restart) — transparent to calling code
  axios.interceptors.response.use(null, async (error) => {
    const config = error.config;
    const status = error?.response?.status;
    const url = config?.url || '';

    if (!(status === 502 || status === 503 || status === 520) || !config) {
      return Promise.reject(error);
    }

    // Never retry polling endpoints — they'll retry on their next scheduled cycle
    if (_silentEndpoints.some((ep) => url.includes(ep))) {
      return Promise.reject(error);
    }

    if (!config._retryCount) config._retryCount = 0;
    if (config._retryCount < 2) {
      config._retryCount++;
      const retryAfter = error?.response?.headers?.['retry-after'];
      const serverDelay = retryAfter ? parseInt(retryAfter, 10) * 1000 : 0;
      const jitter = Math.random() * 500;
      const delay = Math.max(serverDelay, config._retryCount * 2000) + jitter;
      await new Promise((r) => setTimeout(r, delay));
      return axios(config);
    }
    return Promise.reject(error);
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const now = Date.now();
      const requestUrl = error?.config?.url || '';
      const isSilentPoll = _silentEndpoints.some((ep) => requestUrl.includes(ep));
      // eslint-disable-next-line no-unused-vars
      const isMonitored = _monitoredEndpoints.some((ep) => requestUrl.includes(ep));

      if (isSilentPoll) {
        return Promise.reject(error);
      }

      if (status === 520 || status === 502 || status === 503 || status === 504) {
        _trackServerError();
        if (now - _errorThrottle.server > 60000) {
          _errorThrottle.server = now;
          _sendErrorReport(_buildErrorPayload(status, requestUrl, error));
        }
      } else if (status === 401) {
        const isAuthEndpoint = requestUrl.includes('/auth/') || requestUrl.includes('/ti-admin/auth') || requestUrl.includes('/ti-admin/login');
        // Defensive: never run the sign-out cascade for a 401 from a DIFFERENT
        // origin than the page. A misconfigured cross-origin call (e.g. a baked
        // REACT_APP_BACKEND_URL pointing at another host) won't send our cookie
        // and always 401s — that must not wipe a valid same-origin session.
        let isCrossOrigin = false;
        try {
          if (requestUrl && /^https?:\/\//i.test(requestUrl)) {
            isCrossOrigin = new URL(requestUrl).host !== window.location.host;
          }
        } catch (_e) { /* ignore */ }
        // Admins authenticate via a SEPARATE session (`/api/ti-admin/me`), not the
        // user session (`/api/auth/me`). On admin pages, `/api/auth/me` always
        // returns 401 for an admin-only session — verifying against it would log
        // the admin out on any transient admin-endpoint 401. Pick the correct
        // session endpoint based on the current area.
        const inAdminArea = typeof window !== 'undefined' && window.location.pathname.startsWith('/ti-admin');
        const verifyUrl = `${window.location.origin}${inAdminArea ? '/api/ti-admin/me' : '/api/auth/me'}`;
        if (!isAuthEndpoint && !isCrossOrigin && now - _errorThrottle.auth > 15000) {
          _errorThrottle.auth = now;
          // Double verification: two separate checks 2 seconds apart
          fetch(verifyUrl, {
            credentials: 'include',
            signal: AbortSignal.timeout(8000),
          })
            .then((res) => {
              if (res.status === 401) {
                setTimeout(() => {
                  fetch(verifyUrl, {
                    credentials: 'include',
                    signal: AbortSignal.timeout(8000),
                  })
                    .then((res2) => {
                      if (res2.status === 401 && _onSessionExpired) {
                        _onSessionExpired();
                      }
                    })
                    .catch(() => {});
                }, 2000);
              }
            })
            .catch(() => {});
        }
      } else if (status === 429) {
        if (now - _errorThrottle.rateLimit > 15000) {
          _errorThrottle.rateLimit = now;
          const retryAfter = error?.response?.headers?.['retry-after'];
          const waitMsg = retryAfter ? ` Please wait ${retryAfter} seconds.` : ' Please wait a moment.';
          toast.warning(`You're doing that too fast.${waitMsg}`, { duration: 5000 });
        }
      } else if (status === 500) {
        _trackServerError();
        if (now - _errorThrottle.server > 30000) {
          _errorThrottle.server = now;
          _sendErrorReport(_buildErrorPayload(500, requestUrl, error));
        }
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const method = error?.config?.method?.toUpperCase();
        if (method && method !== 'GET' && now - _errorThrottle.timeout > 30000) {
          _errorThrottle.timeout = now;
          toast.error('The request took too long. Please try again.', { duration: 5000 });
        }
      } else if (!error.response && error.message === 'Network Error') {
        const method = error?.config?.method?.toUpperCase();
        if (method && method !== 'GET' && now - _errorThrottle.timeout > 120000) {
          _errorThrottle.timeout = now;
          _sendErrorReport(_buildErrorPayload(0, requestUrl, error));
        }
      }

      return Promise.reject(error);
    },
  );
}
